<template>
  <div class="film-platform">
    <ButterflySvgDefs />
    <FilmHeader
      ref="header"
      :t="t"
      :lang="lang"
      :active-page="page"
      @navigate="go"
      @change-lang="setLang"
      @wallet-connected="onWalletConnected"
    />
    <main>
      <HomePage
        v-if="page === 'home'"
        :t="t"
        :fmt="fmt"
        :lang="lang"
        :chain-projects="chainProjects"
        :chain-loading="chainLoading"
        :active-filter="projectFilter"
        @navigate="go"
        @apply="openApply"
        @explore="scrollToProjects"
        @filter="projectFilter = $event"
        @open-project="openProject"
      />
      <DetailPage
        v-else-if="page === 'detail' && currentProject"
        :t="t"
        :fmt="fmt"
        :lang="lang"
        :project="currentProject"
        :sold="soldOf(currentProject)"
        :holding="holdingOf(currentProject.id)"
        @navigate="go"
        @buy="openBuy"
      />
      <div v-else-if="page === 'detail'" class="wrap" style="padding: 48px 0">
        <div class="panel empty">{{ t("empty_projects") }}</div>
        <button type="button" class="btn" @click="go('home')">{{ t("detail_back") }}</button>
      </div>
      <RulesPage v-else-if="page === 'rules'" :t="t" />
      <DashboardPage
        v-else-if="page === 'dashboard'"
        :t="t"
        :fmt="fmt"
        :orders="dashboardOrders"
        :direct-share="directShare"
        :wallet-connected="walletConnected"
        :wallet-address="accountAddress"
        :ref-link="refLink"
        :subscribed-by-coin="userSubByCoin"
        :rewarded-by-coin="userRewardByCoin"
        @navigate="go"
        @copy-ref="copyRef"
        @open-project="openProject"
      />
    </main>
    <FilmFooter :t="t" />
    <PurchaseModal
      ref="purchaseModal"
      :open="purchaseOpen"
      :t="t"
      :fmt="fmt"
      :project="currentProject || emptyProject"
      :sold="currentProject ? soldOf(currentProject) : 0"
      :max-qty="currentProject ? remainingQty(currentProject) : 1"
      :chain-total-human="purchaseTotalHuman"
      :quote-loading="purchaseQuoteLoading"
      :upline-address="purchaseUplineAddress"
      :upline-loading="purchaseUplineLoading"
      @close="purchaseOpen = false"
      @confirm="onPurchaseConfirm"
      @quote="refreshPurchaseQuote"
    />
    <ApplyModal
      :open="applyOpen"
      :t="t"
      :fmt="fmt"
      :submitting="applySubmitting"
      @close="closeApply"
      @launch="onApplyLaunch"
      @toast="showToastKey"
    />
    <FilmToast :message="toastMessage" :visible="toastVisible" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ButterflySvgDefs from "@/components/film/ButterflySvgDefs.vue";
import FilmHeader from "@/components/film/FilmHeader.vue";
import FilmFooter from "@/components/film/FilmFooter.vue";
import FilmToast from "@/components/film/FilmToast.vue";
import PurchaseModal from "@/components/film/PurchaseModal.vue";
import ApplyModal from "@/components/film/ApplyModal.vue";
import HomePage from "@/views/film/pages/HomePage.vue";
import DetailPage from "@/views/film/pages/DetailPage.vue";
import RulesPage from "@/views/film/pages/RulesPage.vue";
import DashboardPage from "@/views/film/pages/DashboardPage.vue";
import {
  createTranslator,
  getStoredLang,
  setStoredLang,
} from "@/film/i18n";
import { fmtMessage } from "@/film/format";
import {
  fetchChainProjects,
  findChainProject,
  chainProjectIdForIndex,
} from "@/film/chainProjects";
import {
  createEmptyCoinAmounts,
  mapChainOrders,
  mapUserSubAndRewardData,
} from "@/film/chainDashboard";
import {
  resolveEffectiveUplineAddress,
  resolveParentFromUrl,
} from "@/film/referral";
import { PAY_TOKEN_BY_SYMBOL } from "@/contract/constants";
import {
  calculateTotalPriceWei,
  crowdFundingOnChain,
  fetchDirectShare,
  fetchUserOrders,
  fetchUserSubAndRewardData,
  launchProjectOnChain,
} from "@/contract/funding";
import { fromTokenWei, toTokenWei, getTokenDecimals } from "@/contract/tokens";
import { createMetadataJson } from "@/film/api/metadata";
import { fmtCoin } from "@/film/pricing";
import { shouldNotifyRpcError } from "@/contract/index";
import { assertPayTokenSupported } from "@/contract/fundingPayTokens";
import { formatContractError } from "@/contract/contractErrors";

const VALID_PAGES = new Set(["home", "detail", "rules", "dashboard"]);

const emptyProject = {
  id: "",
  name: "",
  coin: "AAPLB",
  mode: "fixed",
  fixed: 0,
  supply: 0,
};

export default {
  name: "FilmPlatform",
  components: {
    ButterflySvgDefs,
    FilmHeader,
    FilmFooter,
    FilmToast,
    PurchaseModal,
    ApplyModal,
    HomePage,
    DetailPage,
    RulesPage,
    DashboardPage,
  },
  data() {
    const { lang, tr } = createTranslator(getStoredLang());
    return {
      lang,
      tr,
      page: "home",
      currentProjectId: "",
      chainProjects: [],
      chainLoading: false,
      dashboardOrders: [],
      directShare: [],
      userSubByCoin: createEmptyCoinAmounts(),
      userRewardByCoin: createEmptyCoinAmounts(),
      projectFilter: "all",
      purchaseOpen: false,
      purchaseTotalHuman: "",
      purchaseQuoteLoading: false,
      purchaseUplineAddress: "",
      purchaseUplineLoading: false,
      applyOpen: false,
      applySubmitting: false,
      toastMessage: "",
      toastVisible: false,
      toastTimer: null,
      pendingBuy: false,
      emptyProject,
    };
  },
  computed: {
    ...mapGetters(["accountAddress", "isWalletConnected"]),
    walletConnected() {
      return this.isWalletConnected;
    },
    t() {
      return this.tr;
    },
    currentProject() {
      return findChainProject(this.chainProjects, this.currentProjectId);
    },
    refLink() {
      if (!this.walletConnected) return this.t("ref_connect");
      const url = new URL(window.location.href);
      url.hash = "dashboard";
      url.search = "";
      if (this.accountAddress) {
        url.searchParams.set("ref", this.accountAddress);
      }
      return url.href;
    },
  },
  watch: {
    lang(code) {
      const next = createTranslator(code);
      this.tr = next.tr;
      document.documentElement.lang = code === "zh" ? "zh-CN" : code;
    },
    page(name) {
      document.title = this.pageTitle(name);
      window.scrollTo(0, 0);
      if (name === "dashboard" && this.walletConnected) {
        this.refreshDashboard();
      }
    },
    walletConnected(connected) {
      if (connected) {
        this.refreshDashboard();
      } else {
        this.dashboardOrders = [];
        this.directShare = [];
        this.userSubByCoin = createEmptyCoinAmounts();
        this.userRewardByCoin = createEmptyCoinAmounts();
      }
    },
    purchaseOpen(open) {
      if (open) {
        this.refreshPurchaseQuote();
        this.refreshPurchaseUpline();
      }
    },
  },
  created() {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (VALID_PAGES.has(hash)) this.page = hash;
    const params = new URLSearchParams(window.location.search);
    const projectParam = params.get("project");
    if (projectParam) {
      this.currentProjectId = projectParam.toLowerCase();
      if (VALID_PAGES.has(hash) && hash === "detail") this.page = "detail";
    }
    document.title = this.pageTitle(this.page);
    this.refreshChainProjects();
  },
  methods: {
    fmt(key, vars) {
      return fmtMessage(this.t(key), vars);
    },
    soldOf(project) {
      return Math.max(0, Math.floor(Number(project.sold) || 0));
    },
    remainingQty(project) {
      return project.supply - this.soldOf(project);
    },
    holdingOf(projectId) {
      return this.dashboardOrders
        .filter((o) => o.projectId === projectId)
        .reduce((s, o) => s + o.qty, 0);
    },
    setLang(code) {
      setStoredLang(code);
      this.lang = code;
    },
    go(page) {
      if (!VALID_PAGES.has(page)) return;
      this.page = page;
      window.location.hash = page;
    },
    openProject(projectId) {
      this.currentProjectId = projectId;
      this.go("detail");
    },
    pageTitle(page) {
      const name = this.currentProject?.name;
      const map = {
        home: "蝴蝶影视平台 · 让好故事被看见",
        detail: name ? `${name} · 项目详情` : "蝴蝶影视平台 · 项目详情",
        rules: "蝴蝶影视平台 · 平台规则",
        dashboard: "蝴蝶影视平台 · 个人中心",
      };
      return map[page] || map.home;
    },
    showToastKey(key, vars) {
      this.showToast(vars ? this.fmt(key, vars) : this.t(key));
    },
    showToast(message) {
      this.toastMessage = message;
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2800);
    },
    scrollToProjects() {
      this.$nextTick(() => {
        const el = document.getElementById("projects");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    requireWallet() {
      const header = this.$refs.header;
      if (this.walletConnected) return true;
      if (header && header.requireWallet) header.requireWallet();
      else this.showToastKey("toast_connect");
      return false;
    },
    async refreshChainProjects() {
      this.chainLoading = true;
      try {
        this.chainProjects = await fetchChainProjects();
        if (
          this.currentProjectId &&
          !findChainProject(this.chainProjects, this.currentProjectId)
        ) {
          this.currentProjectId = "";
        }
      } catch (err) {
        console.error(err);
        if (shouldNotifyRpcError(err)) {
          this.showToastKey("toast_chain_fail");
        }
        this.chainProjects = [];
      } finally {
        this.chainLoading = false;
        if (this.walletConnected) {
          this.refreshDashboard();
        }
      }
    },
    async refreshDashboard() {
      if (!this.accountAddress) return;
      try {
        const [rawOrders, direct, subRewardRaw] = await Promise.all([
          fetchUserOrders(this.accountAddress),
          fetchDirectShare(this.accountAddress),
          fetchUserSubAndRewardData(this.accountAddress),
        ]);
        console.log(subRewardRaw,'subRewardRaw');
        this.directShare = direct || [];
        this.dashboardOrders = await mapChainOrders(rawOrders, this.chainProjects);
        const { subscribed, rewarded } = await mapUserSubAndRewardData(
          subRewardRaw
        );
        this.userSubByCoin = subscribed;
        this.userRewardByCoin = rewarded;
      } catch (err) {
        console.error(err);
        if (shouldNotifyRpcError(err)) {
          this.showToastKey("toast_chain_fail");
        }
      }
    },
    async refreshPurchaseUpline() {
      this.purchaseUplineLoading = true;
      try {
        this.purchaseUplineAddress = await resolveEffectiveUplineAddress(
          this.accountAddress
        );
      } catch (err) {
        console.error(err);
        this.purchaseUplineAddress = resolveParentFromUrl();
      } finally {
        this.purchaseUplineLoading = false;
      }
    },
    async refreshPurchaseQuote(qtyOverride) {
      const project = this.currentProject;
      const modal = this.$refs.purchaseModal;
      const qty = qtyOverride || modal?.quantity || 1;
      if (!project?.cardAddress) {
        this.purchaseTotalHuman = "";
        return;
      }
      this.purchaseQuoteLoading = true;
      try {
        const wei = await calculateTotalPriceWei(project.cardAddress, qty);
        const human = fromTokenWei(wei, project.tokenDecimals);
        this.purchaseTotalHuman = `${fmtCoin(Number(human), project.coin)} ${project.coin}`;
      } catch (err) {
        console.error(err);
        this.purchaseTotalHuman = "";
      } finally {
        this.purchaseQuoteLoading = false;
      }
    },
    openBuy() {
      const project = this.currentProject;
      if (!project) return;
      if (!project.cardAddress) {
        this.showToastKey("toast_no_card_address");
        return;
      }
      const sold = this.soldOf(project);
      if (sold >= project.supply || project.isFinish) {
        this.showToastKey("toast_soldout");
        return;
      }
      if (!this.requireWallet()) {
        this.pendingBuy = true;
        return;
      }
      this.purchaseOpen = true;
    },
    async onPurchaseConfirm({ quantity, riskChecked }) {
      if (!this.requireWallet()) return;
      const project = this.currentProject;
      if (!project?.cardAddress) {
        this.showToastKey("toast_no_card_address");
        return;
      }
      const sold = this.soldOf(project);
      const left = project.supply - sold;
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > left) {
        this.showToastKey("toast_qty", { max: left });
        return;
      }
      if (!riskChecked) {
        this.showToastKey("toast_risk");
        return;
      }
      this.showToastKey("toast_subscribing");
      try {
        const totalWei = await calculateTotalPriceWei(project.cardAddress, quantity);
        const parent = await resolveEffectiveUplineAddress(this.accountAddress);
        this.purchaseUplineAddress = parent;
        await crowdFundingOnChain({
          cardAddress: project.cardAddress,
          parent,
          quantity,
          payToken: project.payToken,
          totalPriceWei: totalWei,
        });
        this.purchaseOpen = false;
        await this.refreshChainProjects();
        await this.refreshDashboard();
        this.go("dashboard");
        this.showToastKey("toast_subscribe_ok");
      } catch (err) {
        console.error(err);
        this.showToast(formatContractError(err));
      }
    },
    onWalletConnected() {
      this.showToastKey("toast_connected");
      this.refreshDashboard();
      if (this.pendingBuy) {
        this.pendingBuy = false;
        this.purchaseOpen = true;
      }
    },
    openApply() {
      if (!this.requireWallet()) return;
      this.applyOpen = true;
    },
    closeApply() {
      this.applyOpen = false;
    },
    async onApplyLaunch(payload) {
      if (!this.requireWallet()) return;
      this.applySubmitting = true;
      try {
        if (!payload.imageUrl) {
          throw new Error("Missing image_url");
        }
        const tokenUri = await createMetadataJson({
          project_name: payload.name,
          description: payload.description,
          issuer: payload.issuer,
          image_url: payload.imageUrl,
        });
        const payToken = PAY_TOKEN_BY_SYMBOL[payload.pricing.coin];
        if (!payToken) throw new Error("Unsupported pay token");
        await assertPayTokenSupported(payToken, payload.pricing.coin);
        const dec =
          payload.pricing.coin === "BNB" ? 18 : await getTokenDecimals(payToken);
        const initPrice =
          payload.pricing.mode === "fixed"
            ? payload.pricing.fixed
            : payload.pricing.step.start;
        const increment =
          payload.pricing.mode === "fixed" ? 0 : payload.pricing.step.inc;
        const initPriceWei = toTokenWei(initPrice, dec);
        const incrementWei = toTokenWei(increment || 0, dec);
        const newIndex = await launchProjectOnChain({
          payToken,
          initPriceWei,
          incrementWei,
          launchAmount: payload.supply,
          tokenUri,
        });
        this.applyOpen = false;
        await this.refreshChainProjects();
        this.openProject(chainProjectIdForIndex(newIndex));
        this.showToastKey("toast_launch_ok");
      } catch (err) {
        console.error(err);
        this.showToast(formatContractError(err));
      } finally {
        this.applySubmitting = false;
      }
    },
    async copyRef() {
      if (!this.requireWallet()) return;
      try {
        await navigator.clipboard.writeText(this.refLink);
        this.showToastKey("toast_copied");
      } catch {
        this.showToastKey("toast_copy_manual");
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/film-platform.scss";
</style>
